// jarvis-frontend/src/components/AutomationDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  ClockIcon, 
  CogIcon, 
  PlayIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  ChartBarIcon,
  WrenchScrewdriverIcon,
  ComputerDesktopIcon
} from '@heroicons/react/24/outline';

const AutomationDashboard = () => {
  const [automationStatus, setAutomationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentRuns, setRecentRuns] = useState([]);
  const [weeklySummaries, setWeeklySummaries] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [apiAvailable, setApiAvailable] = useState(false);

  const fetchAllData = useCallback(async () => {
    try {
      await Promise.all([
        fetchAutomationStatus(),
        fetchRecentRuns(),
        fetchWeeklySummaries()
      ]);
    } catch (err) {
      console.error('Failed to fetch all data:', err);
    }
  }, []);

  const checkApiAvailability = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/automation/health', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setApiAvailable(true);
        await fetchAllData();
      } else if (response.status === 404) {
        setError('Automation API not available. Please ensure Phase 6 is properly installed.');
        setApiAvailable(false);
      } else {
        const errorText = await response.text();
        setError(`API Error: ${response.status} - ${errorText}`);
        setApiAvailable(false);
      }
    } catch (err) {
      console.error('API availability check failed:', err);
      setError(`Connection failed: ${err.message}. Please check if the backend is running.`);
      setApiAvailable(false);
    } finally {
      setLoading(false);
    }
  }, [fetchAllData]);

  useEffect(() => {
    checkApiAvailability();
  }, [checkApiAvailability]);

  // NEW: Show message when automation isn't installed
  if (!apiAvailable) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center">
          <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 mr-2" />
          <div>
            <h3 className="text-yellow-800 font-medium">Automation Not Available</h3>
            <p className="text-yellow-700 mt-1">
              The automation system is not yet installed or configured. Please complete Phase 6 setup.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const fetchAutomationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/automation/status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // ✅ NEW: Check content type before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Expected JSON, got: ${contentType}. Response: ${text.substring(0, 200)}`);
      }
      
      const data = await response.json();
      if (data.success && data.automation_status) {
        setAutomationStatus(data.automation_status);
      } else {
        throw new Error('Invalid response format from automation status endpoint');
      }
    } catch (err) {
      console.error('Failed to fetch automation status:', err);
      setError(`Status fetch failed: ${err.message}`); // ✅ More specific error
    }
  };

  const fetchRecentRuns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/automation/runs?limit=10', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecentRuns(data.runs || []);
      }
    } catch (err) {
      console.error('Failed to fetch recent runs:', err);
    }
  };

  const fetchWeeklySummaries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/automation/summaries?limit=5', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWeeklySummaries(data.summaries || []);
      }
    } catch (err) {
      console.error('Failed to fetch weekly summaries:', err);
    }
  };

  const triggerTask = async (taskType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/automation/trigger', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          task_type: taskType,
          reason: 'Manual trigger from dashboard'
        })
      });
      
      if (response.ok) {
        await response.json(); // ✅ Fixed: consume response without storing
        alert(`Task ${taskType} triggered successfully!`);
        setTimeout(() => {
          fetchAutomationStatus();
          fetchRecentRuns();
        }, 1000);
      } else {
        const contentType = response.headers.get('content-type');
        let errorMessage = `HTTP ${response.status}`;
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorMessage;
          } catch (parseErr) {
            errorMessage = await response.text();
          }
        } else {
          errorMessage = await response.text();
        }
        
        alert(`Failed to trigger task: ${errorMessage}`);
      }
    } catch (err) {
      alert(`Error triggering task: ${err.message}`);
    }
  };

  const getTaskIcon = (taskType) => {
    const icons = {
      memory_update: WrenchScrewdriverIcon,
      classifier_retrain: ComputerDesktopIcon,
      summary_generation: DocumentTextIcon,
      model_finetuning: ChartBarIcon
    };
    return icons[taskType] || CogIcon;
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'text-green-500',
      running: 'text-blue-500',
      failed: 'text-red-500',
      pending: 'text-yellow-500'
    };
    return colors[status] || 'text-gray-500';
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: CheckCircleIcon,
      running: ClockIcon,
      failed: XCircleIcon,
      pending: ClockIcon
    };
    const IconComponent = icons[status] || ClockIcon;
    return <IconComponent className={`h-5 w-5 ${getStatusColor(status)}`} />;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="ml-4 text-gray-600">Loading automation dashboard...</span> {/* ✅ Added text */}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-red-800 font-medium">Automation Dashboard Error</h3>
            <p className="text-red-700 mt-1">{error}</p>
            
            {/* ✅ NEW: Detailed troubleshooting steps */}
            <div className="mt-4 space-y-2 text-sm text-red-600">
              <p><strong>Troubleshooting steps:</strong></p>
              <ol className="list-decimal list-inside space-y-1 ml-4">
                <li>Ensure the backend server is running</li>
                <li>Verify Phase 6 automation module is installed</li>
                <li>Check that automation routes are included in main.py</li>
                <li>Confirm you have admin permissions</li>
              </ol>
            </div>
            
            {/* ✅ NEW: Retry button */}
            <button 
              onClick={checkApiAvailability}
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
            >
              Retry Connection
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Automation Dashboard</h2>
        <p className="text-gray-600">Monitor and manage weekly automation tasks</p>
        
        {/* System Status */}
        <div className="mt-4 flex items-center space-x-4">
          <div className="flex items-center">
            {automationStatus?.scheduler_running ? (
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
            )}
            <span className="text-sm">
              Scheduler: {automationStatus?.scheduler_running ? 'Running' : 'Stopped'}
            </span>
          </div>
          <div className="text-sm text-gray-500">
            Active Jobs: {automationStatus?.active_jobs || 0}
          </div>
          <div className="text-sm text-gray-500">
            Current Runs: {automationStatus?.current_runs || 0}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'overview', name: 'Overview' },
              { id: 'tasks', name: 'Tasks' },
              { id: 'runs', name: 'Recent Runs' },
              { id: 'summaries', name: 'Weekly Summaries' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {automationStatus?.tasks && Object.keys(automationStatus.tasks).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {Object.entries(automationStatus.tasks).map(([taskType, taskInfo]) => {
                    const IconComponent = getTaskIcon(taskType);
                    return (
                      <div key={taskType} className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <IconComponent className="h-8 w-8 text-blue-500" />
                          <div className="flex items-center space-x-2">
                            {taskInfo.is_enabled ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                        
                        <h3 className="font-medium text-gray-900 mb-1">
                          {taskType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </h3>
                        
                        <p className="text-sm text-gray-500 mb-2">
                          {taskInfo.is_enabled ? 'Enabled' : 'Disabled'}
                        </p>
                        
                        {taskInfo.last_run && (
                          <div className="text-xs text-gray-400">
                            Last run: {taskInfo.last_run.status}
                            {taskInfo.last_run.started_at && (
                              <div>
                                {formatDate(taskInfo.last_run.started_at)}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {taskInfo.is_enabled && (
                          <button
                            onClick={() => triggerTask(taskType)}
                            className="mt-3 w-full bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 transition-colors"
                          >
                            Trigger Now
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ): (
                // ✅ NEW: Show helpful message when no tasks found
                <div className="text-center py-8">
                  <CogIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Automation Tasks Found</h3>
                  <p className="text-gray-500">
                    Automation tasks are not configured yet. Please check your Phase 6 installation.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="space-y-4">
              {automationStatus?.tasks && Object.keys(automationStatus.tasks).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {Object.entries(automationStatus.tasks).map(([taskType, taskInfo]) => {
                    const IconComponent = getTaskIcon(taskType);
                    return (
                      <div key={taskType} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <IconComponent className="h-6 w-6 text-blue-500" />
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {taskType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </h3>
                              <p className="text-sm text-gray-500">
                                Schedule: {taskInfo.schedule || 'Not scheduled'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              taskInfo.is_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {taskInfo.is_enabled ? 'Enabled' : 'Disabled'}
                            </span>
                            
                            {taskInfo.is_enabled && (
                              <button
                                onClick={() => triggerTask(taskType)}
                                className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 transition-colors"
                              >
                                <PlayIcon className="h-4 w-4 inline mr-1" />
                                Trigger
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {taskInfo.last_run && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center space-x-2">
                                {getStatusIcon(taskInfo.last_run.status)}
                                <span>Last run: {taskInfo.last_run.status}</span>
                              </div>
                              {taskInfo.last_run.duration && (
                                <span className="text-gray-500">
                                  Duration: {formatDuration(taskInfo.last_run.duration)}
                                </span>
                              )}
                            </div>
                            
                            {taskInfo.last_run.error && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                                Error: {taskInfo.last_run.error}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ): (
                // ✅ NEW: Show helpful message when no tasks found
                <div className="text-center py-8">
                  <CogIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Automation Tasks Found</h3>
                  <p className="text-gray-500">
                    Automation tasks are not configured yet. Please check your Phase 6 installation.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'runs' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Recent Automation Runs</h3>
              {recentRuns.length === 0 ? (
                <p className="text-gray-500">No automation runs found.</p>
              ) : (
                <div className="space-y-3">
                  {recentRuns.map((run) => {
                    const IconComponent = getTaskIcon(run.task_type);
                    return (
                      <div key={run.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <IconComponent className="h-5 w-5 text-blue-500" />
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {run.task_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </h4>
                              <p className="text-sm text-gray-500">
                                {formatDate(run.started_at)}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            {getStatusIcon(run.status)}
                            <span className="text-sm">
                              {run.duration_seconds ? formatDuration(run.duration_seconds) : 'N/A'}
                            </span>
                          </div>
                        </div>
                        
                        {run.error_message && (
                          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                            {run.error_message}
                          </div>
                        )}
                        
                        {run.results && (
                          <div className="mt-3 text-sm text-gray-600">
                            <strong>Results:</strong> 
                            {run.results.items_processed && ` ${run.results.items_processed} items processed`}
                            {run.results.success && ` • Success`}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'summaries' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Weekly Summaries</h3>
              {weeklySummaries.length === 0 ? (
                <p className="text-gray-500">No weekly summaries available yet.</p>
              ) : (
                <div className="space-y-4">
                  {weeklySummaries.map((summary) => (
                    <div key={summary.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">
                          Week of {new Date(summary.week_start).toLocaleDateString()}
                        </h4>
                        <span className="text-sm text-gray-500">
                          Generated {formatDate(summary.generated_at)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{summary.total_users_active}</div>
                          <div className="text-xs text-gray-500">Active Users</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{summary.total_messages}</div>
                          <div className="text-xs text-gray-500">Messages</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">{summary.total_sessions}</div>
                          <div className="text-xs text-gray-500">Sessions</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-orange-600">
                            {summary.success_rate ? `${(summary.success_rate * 100).toFixed(1)}%` : 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">Success Rate</div>
                        </div>
                      </div>
                      
                      {summary.summary_text && (
                        <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
                          <div className="whitespace-pre-line">{summary.summary_text}</div>
                        </div>
                      )}
                      
                      {summary.insights && summary.insights.key_insights && summary.insights.key_insights.length > 0 && (
                        <div className="mt-3">
                          <h5 className="font-medium text-gray-900 mb-2">Key Insights:</h5>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {summary.insights.key_insights.map((insight, index) => (
                              <li key={index} className="flex items-start">
                                <span className="text-blue-500 mr-2">•</span>
                                {insight}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutomationDashboard;